import React, { useState, useRef, useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidationSummary, ValidationForm, ValidatingInput } from '@/components/forms/validation';
import {
    updateUserSettings,
    // sendConfirmationEmail,
    getUserAvatarThumbnailUrl,
    changeAvatar,
    getUserSettings,
} from '@/services/users';
import { Button } from '@/components/Buttons';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { classnames } from '@/utils/html';
import theme from './UserChangeInfo.module.scss';
import { useAppDispatch, useAsyncState, useGetCorrectLanguageCodeForPhoneInput, useTimestamp } from '@/utils/hooks';
import ImageUpload from '@/components/generic-controls/ImageUpload';
import { loadUser } from '@/state/global/creators';
import { useSelector } from 'react-redux';
import { LoadingIndicator, UserSalutation, USER_SALUTATION_OPTIONS, UserTitle, USER_TITLE_OPTIONS, ValidatingSelect, PhoneInput, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { IState } from '@/state/IState';

const UserChangeInfo: React.FC = () => {
    const [userSettings, , reloadUserSettings] = useAsyncState(() => getUserSettings(), [], null);
    const [saving, setSaving] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [firstName, setFirstName] = useState(userSettings ? userSettings.firstName : '');
    const [lastName, setLastName] = useState(userSettings ? userSettings.lastName : '');
    const [phoneNumber, setPhoneNumber] = useState<string | null>(userSettings ? userSettings.phoneNumber : '');
    const [title, setTitle] = useState<UserTitle | null>(userSettings ? userSettings.title : null);
    const [salutation, setSalutation] = useState<UserSalutation | null>(userSettings ? userSettings.salutation : null);
    const currentLanguagePhoneLang = useGetCorrectLanguageCodeForPhoneInput();
    // const [address, setAddress] = useState(userSettings ? userSettings.address : '');
    // const [confirmResent, setConfirmResent] = useState(false);
    const focusRef = useRef<HTMLInputElement>(null);

    const user = useSelector((state: IState) => state.global.user);

    const cacheBuster = useTimestamp([user]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        if (userSettings != null) {
            setFirstName(userSettings.firstName || '');
            setLastName(userSettings.lastName || '');
            setPhoneNumber(userSettings.phoneNumber || '');
            // setAddress(userSettings.address || '');
            setTitle(userSettings.title || null);
            setSalutation(userSettings.salutation || null);
        }
    }, [userSettings]);

    useEffect(() => {
        if (focusRef.current) {
            focusRef.current.focus();
        }
    }, [isEdit]);

    const reset = () => {
        setFirstName(userSettings ? userSettings.firstName : '');
        setLastName(userSettings ? userSettings.lastName : '');
        setPhoneNumber(userSettings ? userSettings.phoneNumber : '');
        setTitle(userSettings ? userSettings.title : null);
        setSalutation(userSettings ? userSettings.salutation : null);
        // setAddress(userSettings ? userSettings.address : '');
    };

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            await updateUserSettings(firstName, lastName, phoneNumber, title || null, salutation || null);

            triggerHandler(reloadUser);

            Toast.success(<Trans>Settings updated successfully</Trans>);
            setIsEdit(false);
            reset();
        } finally {
            setSaving(false);
        }
    };

    // const resendConfirmationEmail = async () => {
    //     setConfirmResent(true);
    //     await sendConfirmationEmail();
    //     Toast.success(<Trans>Email sent</Trans>);
    // };

    const enabledInputProps = {
        className: 'form-control',
    };

    const disabledInputProps = {
        className: 'form-control-plaintext',
        disabled: true,
    };

    const inputProps = isEdit ? enabledInputProps : disabledInputProps;

    if (!userSettings) {
        return <LoadingIndicator />;
    }

    // const renderResendConfirm = () => {
    //     if (userSettings.emailConfirmed) {
    //         return null;
    //     }

    //     if (confirmResent) {
    //         return (
    //             <div className={theme.emailConfirmText}>
    //                 <Trans>Confirmation email has been sent.</Trans>
    //             </div>
    //         );
    //     }

    //     return (
    //         <div className={theme.emailConfirmText}>
    //             <Trans>Email is not confirmed.</Trans>
    //             <Button color="link" onClick={resendConfirmationEmail} size="xsmall">
    //                 <Trans>Resend confirmation email</Trans>
    //             </Button>
    //         </div>
    //     );
    // };

    const handleHeaderImageChange = async (image) => {
        await changeAvatar(image);
        reloadUser();
    };

    const reloadUser = () => {
        loadUser(dispatch);
        reloadUserSettings();
    };

    return (
        <>
            <h5>
                <Trans>Personal information </Trans>
                {!isEdit && (
                    <Button color="link" size="xsmall" onClick={(e) => setIsEdit(true)}>
                        <Trans>Change</Trans>
                    </Button>
                )}
            </h5>
            <ValidationForm onSubmit={onSave} className={classnames('form columns-1 labels-25', theme.form)}>
                <div>
                    <div style={{ alignItems: 'center' }} className="form-label">
                        <Trans>User picture</Trans>
                        <div className="form-control-unstyled">
                            <ImageUpload
                                containerClassName={theme.userImage}
                                aspectRatio={1}
                                currentImage={getUserAvatarThumbnailUrl() + '?' + cacheBuster}
                                onChange={handleHeaderImageChange}
                                previewClassName={theme.headerImageEditPreview}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <label>
                        <div className="form-label">
                            <Trans>First name</Trans>
                        </div>
                        <ValidatingInput
                            placeholder={t`First name`}
                            required
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            {...inputProps}
                            ref={focusRef}
                        />
                    </label>
                </div>
                <div>
                    <label>
                        <Trans>Last name</Trans>
                        <ValidatingInput
                            placeholder={t`Last name`}
                            required
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            {...inputProps}
                        />
                    </label>
                </div>
                {user?.locale === 'de-DE' && (
                    <>
                        <div>
                            <label>
                                <Trans>Title</Trans>
                                {isEdit ? (
                                    <ValidatingSelect
                                        value={title ?? ''}
                                        onChange={(v) => setTitle(v.target.value as UserTitle)}
                                        {...inputProps}>
                                        <option value="">{t`None`}</option>
                                        {USER_TITLE_OPTIONS.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </ValidatingSelect>
                                ) : (
                                    <input value={userSettings.title || t`None`} {...disabledInputProps} />
                                )}
                            </label>
                        </div>
                        <div>
                            <label>
                                <Trans>Salutation</Trans>
                                {isEdit ? (
                                    <ValidatingSelect
                                        value={salutation ?? ''}
                                        onChange={(v) => setSalutation(v.target.value as UserSalutation)}
                                        {...inputProps}>
                                        <option value="">{t`None`}</option>
                                        {USER_SALUTATION_OPTIONS.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </ValidatingSelect>
                                ) : (
                                    <input value={userSettings.salutation || t`None`} {...disabledInputProps} />
                                )}
                            </label>
                        </div>
                    </>
                )}
                <div>
                    <label>
                        <Trans>Email</Trans>
                        <ValidatingInput
                            placeholder={t`Email`}
                            required
                            type="text"
                            // className="form-control-plaintext"
                            value={userSettings.email}
                            {...disabledInputProps}
                        />
                    </label>

                    {/* {renderResendConfirm()} */}
                </div>

                <div>
                    <label>
                        <Trans>Phone number</Trans>
                        {isEdit ? (
                            <div className="form-control-unstyled">
                                <PhoneInput
                                    required
                                    className="form-control"
                                    value={phoneNumber || ''}
                                    onChange={(v) => setPhoneNumber(v)}
                                    fieldName="Phone"
                                    initialCountry={currentLanguagePhoneLang}
                                />
                            </div>
                        ) : (
                            <input value={userSettings.phoneNumber || ''} onChange={(e) => {}} {...disabledInputProps} />
                        )}
                    </label>
                </div>
                {/* <div>
                    <label>
                        <Trans>Address</Trans>
                        <ValidatingInput
                            maxLength={1000}
                            placeholder={t`Address`}
                            type="text"
                            className="form-control-plaintext"
                            value={userSettings.address}
                            {...inputProps}
                            onChange={(e) => setAddress(e.target.value)}
                        />
                    </label>
                </div> */}

                {isEdit && (
                    <>
                        <div className="label-offset">
                            <ValidationSummary />
                        </div>
                        <div className="buttons">
                            <Button
                                color="tertiary"
                                disabled={saving}
                                onClick={(e) => {
                                    reset();
                                    setIsEdit(false);
                                }}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button type="submit" color="primary" loading={saving}>
                                <Trans>Save</Trans>
                            </Button>
                        </div>
                    </>
                )}
            </ValidationForm>
        </>
    );
};
export default UserChangeInfo;
